// src/lib/fundingJourney.js
//
// Adding funds with ETH on Base (mission 005; 01b §3 + §5 for the crypto
// method, 01d items 1–2 after Astra review 03). The journey is durable —
// localStorage, scoped to account + network — so a reload, a remount, another
// panel or another tab resumes the SAME order instead of starting a second.
//
//   generation   every journey has one; every operation and every late result
//                (order status, wallet completion) names the generation and
//                order it belongs to, and is dropped if the journey moved on
//   start        refused while a journey is active unless the caller names the
//                generation it replaces (consent for THIS journey)
//   lock         v3 (Fable decision 04): ONE primitive, withJourneyLock(account,
//                network) over Web Locks (exclusive, same name in every tab).
//                EVERY mutation — start, claim, settle, applyOrderPhase, cancel —
//                runs inside it and re-reads the journey before deciding. No Web
//                Locks → locks_unavailable (the panel disables Deposit); there is
//                no weaker fallback. Read-back verifies persistence, not exclusion.
//   claim        claims EXACTLY the journey the user saw (generation, order,
//                value_wei, pack) as `submitting`, BEFORE the wallet is asked;
//                anything else (a replacement not rendered yet) is `mismatch`
//   submitting / wallet_submission_uncertain  settled only by the order's own
//                outcome (order_status): never re-sent, never replaced silently
//
// States: awaiting_confirmation → submitting → deposit_pending → credited, or
// review / order_expired; a definite user rejection returns to
// awaiting_confirmation; anything else after the wallet was asked is
// wallet_submission_uncertain.
//
// verifyBeforeDeposit is checked right before signing: the signed-in wallet,
// the order's (server-selected) chain, an unexpired order, the exact quoted
// value, and value + gas in the wallet. Never a partial or whole-balance deposit.

export const FUNDING_POLL_MS = 4_000;
const PREFIX = 'lunexx.fundingJourney.v3';
const BUS = 'lunexx:fundingJourney';
const ACTIVE = new Set(['awaiting_confirmation', 'submitting', 'deposit_pending', 'wallet_submission_uncertain', 'review']);
const EXPIRY_MARGIN_MS = 30_000;

const newId = () => (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
const fail = (code, message) => Object.assign(new Error(message), { code });

/** Deposit can be offered: an unclaimed journey awaiting the user's confirmation */
export const canDeposit = (j) => !!j && j.state === 'awaiting_confirmation' && !j.claim;

export function fundingJourneyStore(storage, account, networkId, { locks = defaultLocks() } = {}) {
  const slot = account && networkId ? `${PREFIX}:${String(account).toLowerCase()}:${networkId}` : null;
  const read = () => {
    if (!slot || !storage) return null;
    try { return JSON.parse(storage.getItem(slot) || 'null'); } catch { return null; }
  };
  /** write and verify it persisted (persistence, NOT exclusion — the lock is the exclusion) */
  const write = (v) => {
    if (!slot || !storage) return false;
    try {
      const raw = JSON.stringify(v);
      storage.setItem(slot, raw);
      if (storage.getItem(slot) !== raw) return false;
    } catch { return false; }
    notify(slot);
    return true;
  };
  /** THE coordination primitive: every mutation runs here, exclusively across tabs, and re-reads inside */
  const withJourneyLock = (fn) => {
    if (!locks || !slot) return Promise.reject(fail('locks_unavailable', "This browser can't safely coordinate a deposit."));
    return locks.request(`${slot}:journey`, { mode: 'exclusive' }, async () => fn(read()));
  };
  const refusal = (code) => ({ ok: false, code });
  const soft = (p, onLocksMissing) => p.catch((e) => { if (e?.code === 'locks_unavailable') return onLocksMissing; throw e; });
  return {
    /** a read for rendering; decisions are always re-made inside the lock */
    get: read,
    locked: !!locks && !!slot,
    isActive: () => ACTIVE.has(read()?.state),
    start({ pack, order }, { replaces = null } = {}) {
      return withJourneyLock((cur) => {
        if (cur && ACTIVE.has(cur.state) && cur.generation !== replaces) {
          throw Object.assign(fail('journey_active', 'An earlier deposit is still open.'), { journey: cur });
        }
        const next = { generation: newId(), state: 'awaiting_confirmation', pack, order, txHash: null, claim: null, at: Date.now() };
        if (!write(next)) throw fail('storage_unavailable', 'This browser could not save the deposit, so nothing was started.');
        return next;
      });
    },
    /** claim EXACTLY the rendered journey { generation, orderId, valueWei, packId } for one submission */
    claim(expected, claimId) {
      return soft(withJourneyLock((cur) => {
        if (!cur || cur.generation !== expected?.generation) return cur && ACTIVE.has(cur.state) ? refusal('mismatch') : refusal('stale');
        if (cur.order?.order_id !== expected.orderId || String(cur.order?.call?.value_wei) !== String(expected.valueWei) || cur.pack?.id !== expected.packId) return refusal('mismatch');
        if (cur.claim) return refusal('claimed');
        if (cur.state !== 'awaiting_confirmation') return refusal('not_awaiting');
        const next = { ...cur, state: 'submitting', claim: { id: claimId, at: Date.now() } };
        if (!write(next)) return refusal('storage_unavailable');
        return { ok: true, journey: next };
      }), refusal('locks_unavailable'));
    },
    /** the wallet's answer for a claim: sent { txHash } | rejected | send_uncertain */
    settleSubmission(generation, claimId, ev) {
      return soft(withJourneyLock((cur) => {
        if (!cur || cur.generation !== generation || !cur.claim || cur.claim.id !== claimId) return null;
        const next = ev.kind === 'rejected' ? { ...cur, state: 'awaiting_confirmation', claim: null } : nextJourneyState(cur, ev);
        return write(next) ? next : null;
      }), null);
    },
    applyOrderPhase(generation, orderId, phase) {
      return soft(withJourneyLock((cur) => {
        if (!cur || cur.generation !== generation || cur.order?.order_id !== orderId) return null;   // a late answer: dropped
        const next = nextJourneyState(cur, { kind: 'order_phase', phase });
        if (next === cur) return cur;
        return write(next) ? next : null;
      }), null);
    },
    /** Cancel: only an UNCLAIMED awaiting journey of that generation */
    cancel(generation) {
      return soft(withJourneyLock((cur) => {
        if (!cur || cur.generation !== generation || cur.claim || cur.state !== 'awaiting_confirmation') return false;
        try { storage.removeItem(slot); } catch { return false; }
        notify(slot);
        return true;
      }), false);
    },
    /** changes from this tab's other panels and from other tabs */
    subscribe(fn) {
      if (!slot || !storage) return () => {};
      if (typeof storage.onChange === 'function') return storage.onChange((k) => { if (k === slot) fn(read()); });
      if (typeof window === 'undefined') return () => {};
      const local = (e) => { if (e.detail === slot) fn(read()); };
      const other = (e) => { if (e.key === slot) fn(read()); };
      window.addEventListener(BUS, local);
      window.addEventListener('storage', other);
      return () => { window.removeEventListener(BUS, local); window.removeEventListener('storage', other); };
    },
  };
}

function notify(slot) {
  try { if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(BUS, { detail: slot })); } catch { /* no window */ }
}
function defaultLocks() {
  try { return (typeof navigator !== 'undefined' && navigator.locks) || null; } catch { return null; }
}

export function nextJourneyState(j, ev) {
  if (!j) return j;
  switch (ev.kind) {
    case 'sent': return { ...j, state: 'deposit_pending', txHash: ev.txHash };
    case 'send_uncertain': return { ...j, state: 'wallet_submission_uncertain' };
    case 'cancelled': return j;
    case 'order_phase': {
      const p = ev.phase;
      if (p === 'credited') return { ...j, state: 'credited' };
      if (p === 'needs_review' || p === 'unallocated' || p === 'under_review') return { ...j, state: 'review' };
      if (j.state === 'wallet_submission_uncertain' || j.state === 'submitting') return j;   // only the order's own outcome settles it
      if (p === 'held_until_finality') return { ...j, state: 'deposit_pending' };
      if (p === 'quote_expired') return j.state === 'awaiting_confirmation' ? { ...j, state: 'order_expired' } : j;
      return j;
    }
    default: return j;
  }
}

/** a definite "no" from the user, by structured wallet codes anywhere in the cause chain (never by message text) */
export function isUserRejection(err) {
  let e = err;
  for (let i = 0; e && i < 8; i += 1) {
    if (e.code === 4001 || e.code === 'ACTION_REJECTED' || e.name === 'UserRejectedRequestError') return true;
    e = e.cause;
  }
  return false;
}

/** the exact decimal ETH of a wei amount (no rounding) */
export function weiToEthExact(wei) {
  let v;
  try { v = BigInt(String(wei)); } catch { return null; }
  const neg = v < 0n; if (neg) v = -v;
  const whole = v / 10n ** 18n, frac = (v % 10n ** 18n).toString().padStart(18, '0').replace(/0+$/, '');
  return `${neg ? '-' : ''}${whole}${frac ? `.${frac}` : ''}`;
}

const big = (v) => { try { return v === null || v === undefined ? null : BigInt(v); } catch { return null; } };

export function verifyBeforeDeposit({ authWallet, account, order, now = Date.now(), balanceWei, gasWei }) {
  const call = order?.call;
  if (!call || call.function !== 'deposit(bytes32)' || !/^0x[0-9a-fA-F]{40}$/.test(call.to || '')
      || !/^0x[0-9a-f]{64}$/.test(call.args?.[0] || '') || !/^[0-9]+$/.test(String(call.value_wei || ''))) {
    return { ok: false, code: 'malformed' };
  }
  if (!account?.address || !authWallet || account.address.toLowerCase() !== String(authWallet).toLowerCase()) return { ok: false, code: 'wallet_mismatch' };
  if (account.chainId !== order.chain_id) return { ok: false, code: 'wrong_chain' };
  if (!(Date.parse(order.expires_at) - EXPIRY_MARGIN_MS > now)) return { ok: false, code: 'order_expired' };
  if (order.asset_amount_wei !== undefined && String(order.asset_amount_wei) !== String(call.value_wei)) return { ok: false, code: 'value_mismatch' };
  const bal = big(balanceWei), gas = big(gasWei);
  if (bal === null || gas === null) return { ok: false, code: 'balance_unknown' };
  if (bal < BigInt(call.value_wei) + gas) return { ok: false, code: 'insufficient_eth' };
  return { ok: true, valueWei: String(call.value_wei) };
}
